import ScheduleConflictModal from '~/components/fragments/dialogs/schedule-conflict.vue';
import { getConferenceTimezone } from '~~/shared/utilities/schedule';

// The arrival conflict modal: shown on login and on reaching the agenda when the
// attendee's saved schedule has become self-conflicting (Jack, 2026-08-20).
//
// Ordering matters more than it looks. The Save a Seat return-visit prompt owns
// the first arrival after seats open, and stacking a second modal behind it
// would bury one of them — so this only runs once that prompt has been SEEN and
// dismissed. Before then, conflicts stay reachable through the notification
// banner instead.
//
// Deliberately NOT once-ever, unlike the seat prompt: a conflict is a state the
// attendee is still in, not a notice they have read, so it is raised again each
// login and each visit to the agenda until they resolve it. `shownThisVisit`
// stops it firing twice when both triggers land together — logging in while
// already on the agenda.
const SEAT_PROMPT_SEEN_KEY = 'unbound:seat-prompt-seen';

const seatPromptDismissed = () => {
    try {
        return window.localStorage.getItem(SEAT_PROMPT_SEEN_KEY) === '1';
    } catch {
        // Private mode: treat as seen, matching useSeatPrompt's own fallback, so
        // a browser that cannot remember dismissals doesn't suppress this forever.
        return true;
    }
};

export const useScheduleConflicts = () => {
    const { loggedIn } = useUserSession();
    const { open } = useModal();
    // Per page-load, not per navigation: re-entering the agenda within one visit
    // should not re-raise it.
    const shownThisVisit = useState('scheduleConflictShown', () => false);
    // Held for the whole of an arrival attempt — the conflicts request AND the
    // time the modal is on screen. shownThisVisit alone cannot close the window
    // between the two: it is read at entry and written only once the request
    // comes back, so two triggers landing inside that round trip both passed and
    // both opened. That is the "logging in while already on the agenda" case the
    // comment above claims is covered, and on a phone the slower request made it
    // easy to hit (Nick, Asana 1217793088788278, 2026-08-24).
    const showInFlight = useState('scheduleConflictShowing', () => false);

    // Shared, cached list of the ids currently in conflict, so any component can
    // ask "is this one clashing?" without its own request. The session drawer
    // needs this: it fetches its session straight from /api/v1/sessions and never
    // passes through agenda.vue's applyConflicts overlay, so nothing on the row
    // itself says so.
    //
    // The GROUPS are cached, not just the ids: the drawer's copy changes according
    // to how many other things share the slot, so callers need the neighbours and
    // not merely a yes/no. Plain arrays, because useState is serialised into the
    // SSR payload and a Set does not survive that round trip.
    const conflictGroupList = useState('scheduleConflictGroups', () => null);
    const conflictIdSet = computed(() => new Set(
        (conflictGroupList.value || []).flat().map((i) => i.unique_id),
    ));

    /** True when this session/event id is one half of a clash. */
    const isConflicted = (id) => !!id && conflictIdSet.value.has(id);

    /**
     * The conference-local days that hold a clash, ascending. Calendar can only
     * render one day, so it needs to know which ones are worth opening on.
     * en-CA formats as YYYY-MM-DD, the shape `day` and date_options already use.
     */
    const conflictDays = (year) => {
        const tz = getConferenceTimezone(year);
        const days = new Set();
        for (const item of (conflictGroupList.value || []).flat()) {
            const d = new Date(item.start_date_and_time);
            if (!Number.isNaN(d.getTime())) days.add(d.toLocaleDateString('en-CA', { timeZone: tz }));
        }
        return [...days].sort();
    };

    /**
     * The OTHER items sharing this one's slot — [] when it isn't conflicting.
     * A group is the whole clash including this item, so it is filtered out.
     */
    const conflictPartners = (id) => {
        if (!id) return [];
        const group = (conflictGroupList.value || []).find(
            (g) => g.some((i) => i.unique_id === id),
        );
        return group ? group.filter((i) => i.unique_id !== id) : [];
    };

    /** The caller's current conflict groups, or [] — safe to call logged out. */
    /**
     * Fetch the conflict groups AND prime the shared cache with them.
     *
     * The priming is the point. This used to return the groups to its caller and
     * store nothing, so a component that called it directly — the conflict
     * banner does, on every page — spent the request and left the shared cache
     * empty. Any surface relying on that cache then rendered as though nothing
     * clashed, while the banner sat on screen saying action was needed.
     *
     * That is what made the conflict overlay fail everywhere except the agenda,
     * which keeps its own copy: a speaker page showed a clashing session as an
     * ordinary card, with "Added to My Schedule" and "Seat Saved" correct beside
     * it because those come from the reactive session rather than this cache
     * (Asana 1217708959827824).
     *
     * Same rule as loadConflictIds: only a real answer is cached. Logged out or
     * failed leaves the cache untouched so the next caller tries again.
     */
    const fetchConflicts = async () => {
        if (!loggedIn.value) return [];
        const groups = await $fetch('/api/v1/schedule/conflicts')
            .then((r) => r?.groups || [])
            .catch(() => null);
        if (groups === null) return [];
        conflictGroupList.value = groups;
        return groups;
    };

    /**
     * Raise the modal if there is something to raise it about.
     *
     * @param onFix  invoked when they choose "Take Me to Fix This"
     * @param force  bypass the once-per-visit guard AND the seat-prompt gate —
     *               used by the notification banner, where the attendee asked
     *               for it explicitly. It bypasses READING the guard only; a
     *               forced open still marks the visit (see below).
     */
    const maybeShow = async (onFix, { force = false } = {}) => {
        // "Not during SSR", expressed as a window check rather than
        // import.meta.client: the Nuxt define is undefined under vitest, so a
        // guard written that way makes every test of this function pass
        // vacuously against an immediate `false`.
        if (typeof window === 'undefined') return false;
        if (!loggedIn.value) return false;
        if (!force && (shownThisVisit.value || showInFlight.value)) return false;
        // The seat prompt gets the first arrival to itself.
        if (!force && !seatPromptDismissed()) return false;

        // Claimed synchronously, before anything is awaited — that is the whole
        // point of it. Released in the finally so a failed request or an empty
        // result doesn't wedge the arrival check shut for the rest of the visit.
        if (!force) showInFlight.value = true;
        try {
            const groups = await fetchConflicts();
            if (!groups.length) return false;

            // Set on every open, forced or not. Marking only the unforced ones
            // left the banner's explicit open invisible to the guard, and the
            // banner's own "Take Me to Fix This" navigates to the agenda — whose
            // arrival check then found the guard unset and raised a second modal
            // on top of the first (Nick, Asana 1217793088788278).
            shownThisVisit.value = true;

            const result = await open(ScheduleConflictModal, { groups });
            if (result === 'fix') await onFix?.(groups);
            return true;
        } finally {
            if (!force) showInFlight.value = false;
        }
    };

    /**
     * Refresh the cached id list. Resolves to the Set, for callers that want it.
     *
     * Only caches a result it actually got. Both "not logged in" and "the request
     * failed" used to write [] here, and ensureConflictIds retries only while the
     * cache is null — so one early call poisoned the entire visit and every
     * surface read "nothing is clashing" from then on, however many conflicts
     * the attendee had.
     *
     * That is not hypothetical: the session cookie can hydrate AFTER the mount
     * that first asks. On the agenda a later `schedule:changed` refilled it, so
     * the damage only showed on surfaces with no such event — a speaker page
     * rendered a clashing session as an ordinary card while the agenda showed it
     * in red (Nick/Shannon, Asana 1217708959827824).
     *
     * Leaving the cache null means the next caller tries again, which is the
     * whole recovery mechanism.
     */
    const loadConflictIds = async () => {
        // Delegates, so there is ONE fetch path and ONE place that writes the
        // cache. Two of them is how the banner came to spend a request without
        // priming anything.
        await fetchConflicts();
        return conflictIdSet.value;
    };

    /** Load once per visit unless something changed; cheap to call repeatedly. */
    const ensureConflictIds = async () => (
        conflictGroupList.value === null ? loadConflictIds() : conflictIdSet.value
    );

    return {
        maybeShow, fetchConflicts,
        // `conflictIds` is exposed as the shared SET rather than copied into a
        // caller's own ref. Two caches drifted apart: the agenda refreshed its
        // copy on `schedule:changed` while the drawer and the My Event modal read
        // this one, and a My Event edit — which emits no `schedule:changed` at
        // all — refreshed neither. A cleared conflict went on reading as
        // conflicted (Jack, 2026-08-21).
        conflictIds: conflictIdSet,
        isConflicted, conflictPartners, conflictDays,
        loadConflictIds, ensureConflictIds,
    };
};
